/* eslint no-console: "off" */
import { toPairs, isArray, inRange } from "lodash";

const shouldLog = process.env.NODE_ENV !== "production";

const toString = Object.prototype.toString;

function toUrlParams(params: IJfetchUrlParams) {
  const encode = encodeURIComponent;
  return params
    ? toPairs(params)
        .filter(([key, param]) => !!param)
        .map(([key, param]) => {
          const encodedKey = encode(key);

          // handle differences with encoding arrays of strings
          if (!isArray(param)) {
            return `${encodedKey}=${encode(param.toString())}`;
          } else {
            return (param as string[])
              .map(entry => {
                return `${encodedKey}[]=${encode(entry)}`;
              })
              .join("&");
          }
        })
        .join("&")
    : "";
}

interface IJfetchUrlParams {
  [k: string]: string | string[] | number | boolean;
}

// force headers to be an instance of Headers
interface IJfetchRequestInit extends RequestInit {
  headers?: Headers;
}

// custom error for non-2XX responses
class JfetchError extends Error {
  constructor({ url, options, response }, ...params) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, JfetchError);
    }

    this.name = "JfetchError";
    this.response = response;
    this.url = url;
    this.options = options;
    this.statusCode = response.status;
  }

  response: Response;
  url: string;
  options: IJfetchRequestInit;
  statusCode: number;

  json() {
    return this.response.json();
  }
}

class Jfetch {
  static getCSRFToken() {
    const element = document.querySelector('meta[name="csrf-token"]');
    if (element) {
      const content = element.attributes.getNamedItem("content");
      if (content) {
        return content.value;
      }
    }
  }

  static isFormData(val: any) {
    return toString.call(val) === "[object FormData]";
  }

  async request<T = any>(
    url: string,
    options: IJfetchRequestInit = {}
  ): Promise<T> {
    if (!options.headers) {
      options.headers = new Headers();
    }

    const csrfToken = Jfetch.getCSRFToken();
    if (csrfToken) {
      options.headers.append("X-CSRF-Token", csrfToken);
    }

    const bodyisForm = Jfetch.isFormData(options.body);

    if (!options.headers.has("Content-Type") && !bodyisForm) {
      options.headers.append("Content-Type", "application/json");
    }

    if (!bodyisForm && options.body) {
      options.body = JSON.stringify(options.body);
    }

    options.headers.append("Accept", "application/json");

    options.credentials = "same-origin";

    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (e) {
      console.error("JFETCH: Error while fetching data", options);
      throw e;
    }

    // fetch API does not throw on non-2XX status codes, so we take care of that here
    if (!inRange(response.status, 200, 300)) {
      const msg = `JFETCH: Response is not OK (expected 2XX, got ${
        response.status
      })`;
      console.error(msg);
      throw new JfetchError({ response, url, options }, msg);
    }

    if (response.status === 204) {
      if (shouldLog && options.method !== "DELETE") {
        console.warn(`JFETCH: Warning, recieved 204 response from '${url}'`);
      }
      return {} as T;
    } else {
      try {
        return await response.json();
      } catch (e) {
        console.group("JFETCH Error");
        console.error("JFETCH: Error while parsing data.");
        console.error("Response:", response);
        console.groupEnd();
        throw e;
      }
    }
  }

  get<T = any>(
    url: string,
    params: IJfetchUrlParams = {},
    options: IJfetchRequestInit = {}
  ) {
    options.method = "GET";
    return this.request<T>(`${url}?${toUrlParams(params)}`, options);
  }

  post<T = any>(url: string, body: any, options: IJfetchRequestInit = {}) {
    options.method = "POST";
    options.body = body;
    return this.request<T>(url, options);
  }

  put<T = any>(url: string, body: any, options: IJfetchRequestInit = {}) {
    options.method = "PUT";
    options.body = body;
    return this.request<T>(url, options);
  }

  patch<T = any>(url: string, body: any, options: IJfetchRequestInit = {}) {
    options.method = "PATCH";
    options.body = body;
    return this.request<T>(url, options);
  }

  delete<T = any>(url: string, options: IJfetchRequestInit = {}) {
    options.method = "DELETE";
    return this.request<T>(url, options);
  }
}

const jfetch = new Jfetch();

export default jfetch;



// WEBPACK FOOTER //
// ./app/javascript/utils/jfetch.ts